import { create } from 'zustand';
import type { MCQAttemptResponse } from '@/common/api/models';

type Answers = Record<string, string>;

interface McqAttemptState {
  attemptKey: string | null;
  answers: Answers;
  hydratedFromServer: boolean;
  scoredResultsByAttemptKey: Record<string, MCQAttemptResponse>;
  retryClearedByAttemptKey: Record<string, boolean>;
  setAttemptKey: (attemptKey: string) => void;
  hydrateFromServer: (serverAnswers: Answers) => void;
  selectAnswer: (questionId: string, optionId: string) => void;
  recordScoredResults: (attemptKey: string, results: MCQAttemptResponse) => void;
  clearScoredResults: (attemptKey: string) => void;
  markRetryCleared: (attemptKey: string) => void;
  clearRetryCleared: (attemptKey: string) => void;
  resetAttempt: () => void;
}

export const useMcqAttemptStore = create<McqAttemptState>((set) => ({
  attemptKey: null,
  answers: {},
  hydratedFromServer: false,
  scoredResultsByAttemptKey: {},
  retryClearedByAttemptKey: {},
  setAttemptKey: (attemptKey) => set((state) => {
    if (state.attemptKey === attemptKey) return state;
    return {
      attemptKey,
      answers: {},
      hydratedFromServer: false,
    };
  }),
  hydrateFromServer: (serverAnswers) => set((state) => {
    if (state.hydratedFromServer) return state;
    return {
      answers: {
        ...serverAnswers,
        ...state.answers,
      },
      hydratedFromServer: true,
    };
  }),
  selectAnswer: (questionId, optionId) => set((state) => ({
    answers: {
      ...state.answers,
      [questionId]: optionId,
    },
  })),
  recordScoredResults: (attemptKey, results) => set((state) => ({
    scoredResultsByAttemptKey: {
      ...state.scoredResultsByAttemptKey,
      [attemptKey]: results,
    },
  })),
  clearScoredResults: (attemptKey) => set((state) => {
    const scoredResultsByAttemptKey = { ...state.scoredResultsByAttemptKey };
    delete scoredResultsByAttemptKey[attemptKey];
    return { scoredResultsByAttemptKey };
  }),
  markRetryCleared: (attemptKey) => set((state) => ({
    retryClearedByAttemptKey: {
      ...state.retryClearedByAttemptKey,
      [attemptKey]: true,
    },
  })),
  clearRetryCleared: (attemptKey) => set((state) => {
    const retryClearedByAttemptKey = { ...state.retryClearedByAttemptKey };
    delete retryClearedByAttemptKey[attemptKey];
    return { retryClearedByAttemptKey };
  }),
  resetAttempt: () => set({
    answers: {},
    hydratedFromServer: false,
  }),
}));
